import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Users,
  Building,
  Receipt,
  TrendingUp
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { FinanceStatsCard } from '@/widgets/FinanceStatsCard'
import { PieChart } from '@/widgets/PieChart'
import { BarChart } from '@/widgets/BarChart'

import { IncomeExpenseChart } from '@/widgets/IncomeExpenseChart'
import { CashFlowChart } from '@/widgets/CashFlowChart'
import { ProfitLossChart } from '@/widgets/ProfitLossChart'
import { ReceivablesChart } from '@/widgets/ReceivablesChart'
import { PayablesChart } from '@/widgets/PayablesChart'

import { dashboardsService } from '@/services/dashboardsService'
import { FinanceDashboardData, ChartDataPoint, IncomeExpenseData, CashFlowData, ProfitLossData } from '@/api/types'
import { useAuthStore } from '@/store/auth.store'
import { formatNumber, formatCurrency } from '@/lib/format'

// Custom Saudi Riyal Icon Component compatible with LucideIcon signature
// We intentionally render an <img> so the large SVG stays external from the bundle
const SaudiRiyalIcon = (({ className }: { className?: string }) => (
  <img
    src="/images/logos/sr.svg"
    alt="Saudi Riyal"
    className={`${className ?? ''} object-contain`}
  />
)) as unknown as LucideIcon

/**
 * Professional Finance Dashboard with pie charts and bar chart
 * Updated to match the new design requirements
 */
export const FinanceDashboard: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const [data, setData] = useState<FinanceDashboardData | null>(null)
  const [incomeExpenseData, setIncomeExpenseData] = useState<IncomeExpenseData | null>(null)
  const [cashFlowData, setCashFlowData] = useState<CashFlowData | null>(null)
  const [profitLossData, setProfitLossData] = useState<ProfitLossData | null>(null)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [cashFlowYear, setCashFlowYear] = useState(new Date().getFullYear())
  const [profitLossYear, setProfitLossYear] = useState(new Date().getFullYear())
  const [isAccural, setIsAccural] = useState(true)
  const [loading, setLoading] = useState(true)

  // Fetch main dashboard data (only when branch changes)
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.selectedBranch?.branchId) return

      try {
        setLoading(true)
        const financeData = await dashboardsService.getFinanceDashboard(user.selectedBranch.branchId)
        setData(financeData)
      } catch (error) {
        console.error('Error loading dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [user?.selectedBranch?.branchId])

  // Fetch income/expense data separately (only when year or accounting method changes)
  useEffect(() => {
    const fetchIncomeExpenseData = async () => {
      if (!user?.selectedBranch?.branchId) return

      try {
        const incomeExpense = await dashboardsService.getIncomeExpense(
          user.selectedBranch.branchId,
          selectedYear,
          isAccural
        )
        // Validate data before setting
        if (incomeExpense && 
            typeof incomeExpense.totalIncome === 'number' && 
            typeof incomeExpense.totalExpense === 'number' &&
            isFinite(incomeExpense.totalIncome) &&
            isFinite(incomeExpense.totalExpense) &&
            Array.isArray(incomeExpense.monthlyIncExp) &&
            incomeExpense.monthlyIncExp.every(item => 
              typeof item.income === 'number' && 
              typeof item.expense === 'number' &&
              isFinite(item.income) && 
              isFinite(item.expense) &&
              !isNaN(item.income) &&
              !isNaN(item.expense)
            )) {
          setIncomeExpenseData(incomeExpense)
        } else {
          setIncomeExpenseData(null)
        }
      } catch (error) {
        console.error('Error loading income/expense data:', error)
        setIncomeExpenseData(null)
      }
    }

    fetchIncomeExpenseData()
  }, [user?.selectedBranch?.branchId, selectedYear, isAccural])

  // Fetch cash flow data separately (only when year changes)
  useEffect(() => {
    const fetchCashFlowData = async () => {
      if (!user?.selectedBranch?.branchId) return

      try {
        const cashFlow = await dashboardsService.getCashFlow(
          user.selectedBranch.branchId,
          cashFlowYear
        )
        // Validate data before setting
        if (cashFlow && 
            typeof cashFlow.openingCash === 'number' && 
            typeof cashFlow.totalIncoming === 'number' &&
            typeof cashFlow.totalOutgoing === 'number' &&
            typeof cashFlow.closingCash === 'number' &&
            isFinite(cashFlow.openingCash) &&
            isFinite(cashFlow.totalIncoming) &&
            isFinite(cashFlow.totalOutgoing) &&
            isFinite(cashFlow.closingCash) &&
            Array.isArray(cashFlow.monthlyCashFlow) &&
            cashFlow.monthlyCashFlow.every(item => 
              typeof item.cashBalance === 'number' &&
              isFinite(item.cashBalance) &&
              !isNaN(item.cashBalance)
            )) {
          setCashFlowData(cashFlow)
        } else {
          setCashFlowData(null)
        }
      } catch (error) {
        console.error('Error loading cash flow data:', error)
        setCashFlowData(null)
      }
    }

    fetchCashFlowData()
  }, [user?.selectedBranch?.branchId, cashFlowYear])

  // Fetch profit/loss data separately (only when year changes)
  useEffect(() => {
    const fetchProfitLossData = async () => {
      if (!user?.selectedBranch?.branchId) return

      try {
        const profitLoss = await dashboardsService.getProfitLoss(
          user.selectedBranch.branchId,
          profitLossYear
        )
        // Validate data before setting
        if (profitLoss && 
            typeof profitLoss.totalProfit === 'number' &&
            isFinite(profitLoss.totalProfit) &&
            Array.isArray(profitLoss.monthlyProfitLoss) &&
            profitLoss.monthlyProfitLoss.every(item => 
              typeof item.profit === 'number' &&
              isFinite(item.profit) &&
              !isNaN(item.profit)
            )) {
          setProfitLossData(profitLoss)
        } else {
          setProfitLossData(null)
        }
      } catch (error) {
        console.error('Error loading profit/loss data:', error)
        setProfitLossData(null)
      }
    }

    fetchProfitLossData()
  }, [user?.selectedBranch?.branchId, profitLossYear])


  // Handle year change
  const handleYearChange = async (year: number) => {
    setSelectedYear(year)
    if (user?.selectedBranch?.branchId) {
      try {
        const incomeExpense = await dashboardsService.getIncomeExpense(
          user.selectedBranch.branchId,
          year,
          isAccural
        )
        // Validate data before setting
        if (incomeExpense && 
            typeof incomeExpense.totalIncome === 'number' && 
            typeof incomeExpense.totalExpense === 'number' &&
            isFinite(incomeExpense.totalIncome) &&
            isFinite(incomeExpense.totalExpense) &&
            Array.isArray(incomeExpense.monthlyIncExp) &&
            incomeExpense.monthlyIncExp.every(item => 
              typeof item.income === 'number' && 
              typeof item.expense === 'number' &&
              isFinite(item.income) && 
              isFinite(item.expense) &&
              !isNaN(item.income) &&
              !isNaN(item.expense)
            )) {
          setIncomeExpenseData(incomeExpense)
        } else {
          setIncomeExpenseData(null)
        }
      } catch (error) {
        console.error('Error loading income/expense data:', error)
        setIncomeExpenseData(null)
      }
    }
  }

  // Handle accounting method change
  const handleAccountingMethodChange = async (accural: boolean) => {
    setIsAccural(accural)
    if (user?.selectedBranch?.branchId) {
      try {
        const incomeExpense = await dashboardsService.getIncomeExpense(
          user.selectedBranch.branchId,
          selectedYear,
          accural
        )
        // Validate data before setting
        if (incomeExpense && 
            typeof incomeExpense.totalIncome === 'number' && 
            typeof incomeExpense.totalExpense === 'number' &&
            isFinite(incomeExpense.totalIncome) &&
            isFinite(incomeExpense.totalExpense) &&
            Array.isArray(incomeExpense.monthlyIncExp) &&
            incomeExpense.monthlyIncExp.every(item => 
              typeof item.income === 'number' && 
              typeof item.expense === 'number' &&
              isFinite(item.income) && 
              isFinite(item.expense) &&
              !isNaN(item.income) &&
              !isNaN(item.expense)
            )) {
          setIncomeExpenseData(incomeExpense)
        } else {
          setIncomeExpenseData(null)
        }
      } catch (error) {
        console.error('Error loading income/expense data:', error)
        setIncomeExpenseData(null)
      }
    }
  }

  // Handle cash flow year change
  const handleCashFlowYearChange = async (year: number) => {
    setCashFlowYear(year)
    if (user?.selectedBranch?.branchId) {
      try {
        const cashFlow = await dashboardsService.getCashFlow(
          user.selectedBranch.branchId,
          year
        )
        // Validate data before setting
        if (cashFlow && 
            typeof cashFlow.openingCash === 'number' && 
            typeof cashFlow.totalIncoming === 'number' &&
            typeof cashFlow.totalOutgoing === 'number' &&
            typeof cashFlow.closingCash === 'number' &&
            isFinite(cashFlow.openingCash) &&
            isFinite(cashFlow.totalIncoming) &&
            isFinite(cashFlow.totalOutgoing) &&
            isFinite(cashFlow.closingCash) &&
            Array.isArray(cashFlow.monthlyCashFlow) &&
            cashFlow.monthlyCashFlow.every(item => 
              typeof item.cashBalance === 'number' &&
              isFinite(item.cashBalance) &&
              !isNaN(item.cashBalance)
            )) {
          setCashFlowData(cashFlow)
        } else {
          setCashFlowData(null)
        }
      } catch (error) {
        console.error('Error loading cash flow data:', error)
        setCashFlowData(null)
      }
    }
  }

  // Handle profit/loss year change
  const handleProfitLossYearChange = async (year: number) => {
    setProfitLossYear(year)
    if (user?.selectedBranch?.branchId) {
      try {
        const profitLoss = await dashboardsService.getProfitLoss(
          user.selectedBranch.branchId,
          year
        )
        // Validate data before setting
        if (profitLoss && 
            typeof profitLoss.totalProfit === 'number' &&
            isFinite(profitLoss.totalProfit) &&
            Array.isArray(profitLoss.monthlyProfitLoss) &&
            profitLoss.monthlyProfitLoss.every(item => 
              typeof item.profit === 'number' &&
              isFinite(item.profit) &&
              !isNaN(item.profit)
            )) {
          setProfitLossData(profitLoss)
        } else {
          setProfitLossData(null)
        }
      } catch (error) {
        console.error('Error loading profit/loss data:', error)
        setProfitLossData(null)
      }
    }
  }

  if (loading || !data) {
    return (
      <div className="p-6 space-y-5 bg-gradient-to-br from-gray-50 via-white to-gray-100/50 dark:from-black dark:via-gray-950 dark:to-black min-h-screen">
        <div className="animate-pulse space-y-5">
          {/* Header Skeleton */}
          <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 rounded-xl"></div>
              <div className="space-y-2">
                <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-64"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-80"></div>
              </div>
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-28 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50"></div>
            ))}
          </div>

          {/* Charts Skeleton */}
          <div className="space-y-5">
            <div className="flex items-center space-x-3">
              <div className="w-1 h-6 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-48"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-80 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50"></div>
              ))}
            </div>

            <div className="h-96 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50"></div>
          </div>
        </div>
      </div>
    )
  }

  // Prepare data for pie charts with required color property
  const customersData = [
    { name: t('finance.saudiCustomers'), value: data.saudiCustomers, color: '#0f172a' },
    { name: t('finance.nonSaudiCustomers'), value: data.nonSaudiCustomers, color: '#64748b' }
  ]

  const vendorsData = [
    { name: t('finance.saudiVendors'), value: data.saudiVendors, color: '#0f172a' },
    { name: t('finance.nonSaudiVendors'), value: data.nonSaudiVendors, color: '#64748b' }
  ]

  const invoicesData = [
    { name: t('finance.postedInvoices'), value: data.postedInvoices, color: '#0f172a' },
    { name: t('finance.unpostedInvoices'), value: data.unpostedInvoices, color: '#64748b' }
  ]

  // Prepare data for bar chart
  const financialMetricsData: ChartDataPoint[] = [
    { name: t('finance.totalBills'), value: data.totalBills, color: '#0f172a' },
    { name: t('finance.agedReceivables'), value: data.agedReceivables, color: '#1e293b' },
    { name: t('finance.agedPayables'), value: data.agedPayables, color: '#334155' },
    { name: t('finance.settledInvoices'), value: data.settledInvoices, color: '#475569' },
    { name: t('finance.settledBills'), value: data.settledBills, color: '#64748b' }
  ]

  return (
    <div className="p-6 space-y-5 bg-gradient-to-br from-gray-50 via-white to-gray-100/50 dark:from-black dark:via-gray-950 dark:to-black min-h-screen">
      {/* Premium Header Section */}
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl blur-xl opacity-50"></div>
        <div className="relative bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                {t('finance.dashboardTitle')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
                {t('finance.dashboardSubtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Key Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="transform hover:scale-105 transition-all duration-300">
          <FinanceStatsCard
            title={t('finance.totalCustomers')}
            value={formatNumber(data.totalCustomers)}
            icon={Users}
            trend="neutral"
          />
        </div>
        <div className="transform hover:scale-105 transition-all duration-300">
          <FinanceStatsCard
            title={t('finance.totalVendors')}
            value={formatNumber(data.totalVendors)}
            icon={Building}
            trend="neutral"
          />
        </div>
        <div className="transform hover:scale-105 transition-all duration-300">
          <FinanceStatsCard
            title={t('finance.totalInvoices')}
            value={formatNumber(data.postedInvoices + data.unpostedInvoices)}
            icon={Receipt}
            trend="neutral"
          />
        </div>
        <div className="transform hover:scale-105 transition-all duration-300">
          <FinanceStatsCard
            title={t('finance.totalQuotationAmount')}
            value={formatCurrency(data.totalQuotationAmount)}
            icon={SaudiRiyalIcon}
            trend="neutral"
          />
        </div>
      </div>

      {/* Premium Charts Section */}
      <div className="space-y-5">
        {/* Section Header */}
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-1 h-6 bg-gradient-to-b from-primary to-secondary rounded-full"></div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            {t('finance.performanceAnalytics')}
          </h2>
        </div>

        {/* Premium Pie Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          <div className="transform hover:scale-[1.02] transition-all duration-300">
            <PieChart
              title={t('finance.customersBreakdown')}
              description={`${t('common.total')}: ${formatNumber(data.totalCustomers)}`}
              data={customersData}
              height={280}
              className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
            />
          </div>

          <div className="transform hover:scale-[1.02] transition-all duration-300">
            <PieChart
              title={t('finance.vendorsBreakdown')}
              description={`${t('common.total')}: ${formatNumber(data.totalVendors)}`}
              data={vendorsData}
              height={280}
              className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
            />
          </div>

          <div className="transform hover:scale-[1.02] transition-all duration-300">
            <PieChart
              title={t('finance.invoicesStatus')}
              description={`${t('common.total')}: ${formatNumber(data.postedInvoices + data.unpostedInvoices)}`}
              data={invoicesData}
              height={280}
              className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
            />
          </div>
        </div>

        {/* Premium Financial Metrics Bar Chart */}
        <div className="transform hover:scale-[1.01] transition-all duration-300">
          <BarChart
            title={t('finance.financialMetricsOverview')}
            description={t('finance.financialMetricsOverviewDescription')}
            data={financialMetricsData}
            height={340}
            className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
          />
        </div>

        {/* Receivables and Payables Charts - Side by Side */}
        {user?.selectedBranch?.branchId && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Receivables Chart - Left Side */}
            <div className="transform hover:scale-[1.01] transition-all duration-300">
              <ReceivablesChart
                branchId={user.selectedBranch.branchId}
                className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
              />
            </div>
            {/* Payables Chart - Right Side */}
            <div className="transform hover:scale-[1.01] transition-all duration-300">
              <PayablesChart
                branchId={user.selectedBranch.branchId}
                className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
              />
            </div>
          </div>
        )}

        {/* Premium Income and Expense Chart */}
        {user?.selectedBranch?.branchId && (
          <div className="transform hover:scale-[1.01] transition-all duration-300">
            <IncomeExpenseChart
              data={incomeExpenseData}
              branchId={user.selectedBranch.branchId}
              selectedYear={selectedYear}
              isAccural={isAccural}
              onYearChange={handleYearChange}
              onAccountingMethodChange={handleAccountingMethodChange}
              className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
            />
          </div>
        )}

        {/* Cash Flow Chart */}
        {user?.selectedBranch?.branchId && (
          <div className="transform hover:scale-[1.01] transition-all duration-300">
            <CashFlowChart
              data={cashFlowData}
              branchId={user.selectedBranch.branchId}
              selectedYear={cashFlowYear}
              onYearChange={handleCashFlowYearChange}
              className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
            />
          </div>
        )}

        {/* Profit and Loss Chart */}
        {user?.selectedBranch?.branchId && (
          <div className="transform hover:scale-[1.01] transition-all duration-300">
            <ProfitLossChart
              data={profitLossData}
              branchId={user.selectedBranch.branchId}
              selectedYear={profitLossYear}
              onYearChange={handleProfitLossYearChange}
              className="border-gray-200/60 dark:border-gray-700/60 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
            />
          </div>
        )}

      </div>
    </div>
  )
}

export default FinanceDashboard
